import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    category: 'General',
    questions: [
      {
        q: 'How long does waterproofing last?',
        a: 'The lifespan depends on the system used. Crystalline systems last 30+ years, PU membranes 15-20 years, and APP/SBS membranes 20-25 years. We provide system-specific warranties and maintenance guidelines for maximum longevity.'
      },
      {
        q: 'Which waterproofing is best for terrace in India?',
        a: 'For Indian climate, we recommend APP modified bitumen membranes or PU liquid membranes. APP membranes handle extreme heat well, while PU offers seamless coverage. The choice depends on terrace usage, existing structure, and budget. We assess and recommend the most suitable system during technical inspection.'
      },
      {
        q: 'Is waterproofing covered under warranty?',
        a: 'Yes. We provide written warranties ranging from 5-10 years depending on the system used. Our warranty covers material failure and workmanship issues. We also offer annual maintenance contracts for extended protection.'
      }
    ]
  },
  {
    category: 'Technical',
    questions: [
      {
        q: 'What is crystalline waterproofing?',
        a: 'Crystalline waterproofing uses chemical compounds that penetrate concrete and form needle-like crystals. These crystals block water pathways and have self-healing properties. Ideal for basements, water tanks, and structures requiring permanent protection. Complies with IS 2645 standards.'
      },
      {
        q: 'What is PU (Polyurethane) membrane waterproofing?',
        a: 'PU membrane is a liquid-applied, seamless waterproofing system. It forms an elastic coating that adapts to surface movement, resists UV rays, and is walkable. Excellent for terraces, balconies, and wet areas. Meets IS 15065 specifications.'
      },
      {
        q: 'What is the difference between APP and SBS membranes?',
        a: 'APP (Atactic Polypropylene) membranes are heat-resistant and ideal for hot climates. SBS (Styrene-Butadiene-Styrene) membranes are more flexible and better for cold regions. For Indian conditions, we typically recommend APP membranes for terraces and SBS for areas with structural movement.'
      },
      {
        q: 'Do you use food-grade coatings for water tanks?',
        a: 'Yes, we use FSSAI-approved, non-toxic coatings for drinking water tanks. These coatings are bacteria-resistant, prevent algae growth, and meet IS 12288 standards. Safe for potable water storage with 15-20 year lifespan.'
      }
    ]
  },
  {
    category: 'Process & Timeline',
    questions: [
      {
        q: 'How long does site inspection take?',
        a: 'Initial site inspection takes 1-2 hours. For larger projects, we conduct detailed assessment over 1-2 days including moisture mapping and structural evaluation. You receive a comprehensive report with root cause analysis and recommended solution within 48 hours.'
      },
      {
        q: 'What is the typical project timeline?',
        a: 'Timeline varies by scope: Bathroom - 3-4 days, Terrace (1000 sq ft) - 5-7 days, Complete house - 10-15 days, Government projects - as per tender specifications. We provide detailed timeline during BOQ submission.'
      },
      {
        q: 'Will waterproofing work disrupt daily activities?',
        a: 'We minimize disruption through phased execution. Bathrooms can be waterproofed one at a time. Terrace work doesn\'t affect indoor activities. For offices and institutions, we can schedule work during holidays or non-working hours.'
      }
    ]
  },
  {
    category: 'Cost & Payment',
    questions: [
      {
        q: 'How is waterproofing cost calculated?',
        a: 'Cost depends on: area size, system type, surface condition, accessibility, and warranty period. We provide itemized BOQ (Bill of Quantities) after technical assessment. Government projects follow CPWD/PWD rate analysis.'
      },
      {
        q: 'Do you provide payment flexibility?',
        a: 'Yes. For residential clients: 30% advance, 40% at mid-stage, 30% on completion. For government projects: as per tender payment terms. We accept bank transfers, cheques, and digital payments.'
      },
      {
        q: 'Are material test certificates provided?',
        a: 'Yes, we provide: material test certificates, manufacturer certifications, compliance documents (IS/CPWD), work completion certificates, and warranty documentation. Essential for government and institutional projects.'
      }
    ]
  },
  {
    category: 'Government Projects',
    questions: [
      {
        q: 'Do you handle government tenders?',
        a: 'Yes, we have successfully executed 180,000 sq ft of government waterproofing work. We handle CPWD specifications, provide all required documentation, and maintain compliance with government inspection standards.'
      },
      {
        q: 'Which government departments have you worked with?',
        a: 'We have completed projects for: Police Housing Board Jabalpur (1,00,000 sq ft), Control Room Jabalpur Divisions (50,000 sq ft), State Institute of Hotel Management (30,000 sq ft), and other government institutions.'
      },
      {
        q: 'Do you provide contractor registration certificates?',
        a: 'Yes, we maintain all necessary registrations including GST, professional tax, and relevant contractor licenses. We can provide documentation as required for tender submissions.'
      }
    ]
  }
];

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);

  const toggleFAQ = (categoryIndex, questionIndex) => {
    if (openCategory === categoryIndex && openIndex === questionIndex) {
      setOpenIndex(null);
      setOpenCategory(null);
    } else {
      setOpenCategory(categoryIndex);
      setOpenIndex(questionIndex);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Everything you need to know about waterproofing systems, processes, and our services
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqs.map((category, catIndex) => (
            <div key={catIndex} className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-slate-200">
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((faq, qIndex) => (
                  <div
                    key={qIndex}
                    className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(catIndex, qIndex)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-100 transition-colors"
                    >
                      <span className="font-semibold text-slate-900 pr-4">{faq.q}</span>
                      {openCategory === catIndex && openIndex === qIndex ? (
                        <ChevronUp className="w-5 h-5 text-slate-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-600 flex-shrink-0" />
                      )}
                    </button>
                    {openCategory === catIndex && openIndex === qIndex && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Get personalized answers from our technical team
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-slate-800 rounded-lg text-base font-semibold hover:bg-slate-100 transition-colors"
          >
            Talk to Our Experts
          </a>
        </div>
      </section>
    </div>
  );
};